import { SeoHead } from "@/components/SeoHead";
import { PageTransition } from "@/components/layout/PageTransition";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle, Plane, Globe, FileCheck, Home, Users, Shield, LucideIcon } from "lucide-react";
import { useCms } from "@/hooks/useCms";

const ICON_MAP: Record<string, LucideIcon> = {
  Globe, FileCheck, Home, Shield, Users, Plane,
};

const DEFAULT_FEATURES = [
  { icon: "Globe", title: "International Club Network", desc: "Direct relationships with clubs across Germany, UK, Netherlands, Spain, Portugal, USA, Canada, and across Africa." },
  { icon: "FileCheck", title: "Transfer Documentation", desc: "We handle all FIFA transfer paperwork, ITC (International Transfer Certificates), and regulatory compliance from start to finish." },
  { icon: "Home", title: "Relocation Support", desc: "From finding accommodation to settling into a new country — we ensure athletes and their families transition smoothly." },
  { icon: "Shield", title: "Visa & Work Permits", desc: "Expert guidance on obtaining athlete visas and work permits across EU and non-EU countries, including appeals where necessary." },
  { icon: "Users", title: "Club Trial Arrangements", desc: "We organise and facilitate professional club trials, ensuring players get in front of the right coaches and sporting directors." },
  { icon: "Plane", title: "Travel & Logistics", desc: "Full coordination of travel itineraries, accommodation during trials, and logistics management throughout the transfer process." },
];

const DEFAULT_BULLETS = [
  "End-to-end FIFA TMS transfer registration and ITC processing",
  "Dedicated immigration lawyers handling visas and work permits",
  "Trial logistics and player accommodation fully managed",
  "Direct relationships with club sporting directors and chief scouts",
  "On-the-ground support in host countries for cultural and lifestyle integration",
];

export default function InternationalTransfers() {
  const { getMeta, getText } = useCms();
  const meta = getMeta<{
    heading?: string;
    highlight?: string;
    subtext?: string;
    heroImage?: string;
    overviewHeading?: string;
    overviewText?: string;
    overviewImage?: string;
    bullets?: string[];
    features?: { icon: string; title: string; desc: string }[];
  }>("service_transfers", {});

  const heading = meta.heading ?? "INTERNATIONAL";
  const highlight = meta.highlight ?? "TRANSFERS";
  const subtext = meta.subtext ?? getText("service_transfers", "Seamless, fully managed transfers connecting talented athletes with professional clubs across Europe, North America, and beyond.");
  const heroImage = meta.heroImage || "https://images.unsplash.com/photo-1526232761682-d26e03ac148e?w=1600&auto=format&fit=crop";
  const overviewHeading = meta.overviewHeading ?? "CROSSING BORDERS, BUILDING CAREERS";
  const overviewText = meta.overviewText ?? "International transfers are complex — but with the right team, they don't have to be. Eliada Sports Agency has built a robust process for managing every phase of an athlete's move from one country to another, ensuring full regulatory compliance and a smooth personal transition.\n\nWe leverage our global club network and relationships with sporting directors to create real transfer opportunities — not just trials. Our end-to-end service removes every administrative barrier from the athlete's path.";
  const overviewImage = meta.overviewImage || "https://images.unsplash.com/photo-1505666287802-931dc83948e9?w=900&auto=format&fit=crop";
  const bullets = (Array.isArray(meta.bullets) && meta.bullets.length > 0) ? meta.bullets : DEFAULT_BULLETS;
  const features = (Array.isArray(meta.features) && meta.features.length > 0) ? meta.features : DEFAULT_FEATURES;

  return (
    <>
      <SeoHead
        title={`${heading} ${highlight}`}
        description={subtext}
        path="/services/international-transfers"
      />
      <PageTransition className="pt-24 pb-0">
        {/* Hero */}
        <div className="relative min-h-[55vh] flex items-center overflow-hidden">
          <img src={heroImage} alt={`${heading} ${highlight}`} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40" />
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-primary/40 bg-primary/10 text-primary mb-6">
              <Plane size={14} />
              <span className="text-xs font-display tracking-widest uppercase font-semibold">Our Services</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-display font-bold text-white mb-6 max-w-2xl leading-tight">
              {heading} <span className="text-primary">{highlight}</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-xl mb-8 leading-relaxed">
              {subtext}
            </p>
            <Link href="/join" onClick={() => {}}>
              <Button size="lg" className="gap-2">Start Your Transfer <ArrowRight size={18} /></Button>
            </Link>
          </div>
        </div>

        {/* Overview */}
        <section className="py-24 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-4xl font-display font-bold text-gray-900 mb-6">
                  {overviewHeading}
                </h2>
                <div className="space-y-4 mb-8">
                  {overviewText.split("\n\n").map((para, i) => (
                    <p key={i} className="text-gray-600 text-lg leading-relaxed">
                      {para}
                    </p>
                  ))}
                </div>
                <ul className="space-y-3">
                  {bullets.map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-gray-700">
                      <CheckCircle className="text-primary shrink-0 w-5 h-5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative">
                <img src={overviewImage} alt="International Transfers" className="rounded-2xl shadow-2xl w-full object-cover" />
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-24 bg-secondary">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">
                THE TRANSFER <span className="text-primary">PROCESS</span>
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Comprehensive support at every stage of your international move.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((f, i) => {
                const IconComponent = ICON_MAP[f.icon] || Globe;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="bg-background p-8 rounded-2xl border border-white/5 hover:border-primary/40 transition-colors"
                  >
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-5">
                      <IconComponent className="text-primary w-6 h-6" />
                    </div>
                    <h3 className="font-display font-bold text-white text-lg mb-3">{f.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{f.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="w-full bg-gradient-to-r from-yellow-600 via-yellow-500 to-yellow-600 py-20">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-4">READY FOR YOUR NEXT MOVE?</h2>
            <p className="text-xl text-gray-800 mb-10 font-medium">Explore international transfer opportunities with guidance from our experienced management team.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link href="/join" onClick={() => {}}><Button size="lg" className="bg-gray-900 text-white hover:bg-black">Apply Now</Button></Link>
              <Link href="/contact" onClick={() => {}}><Button size="lg" variant="outline" className="border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white bg-transparent">Contact Us</Button></Link>
            </div>
          </div>
        </section>
      </PageTransition>
    </>
  );
}
